import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import ExitIntentPopup from "../components/ExitIntentPopup";
import MobileShowcase from "../components/MobileShowcase";
import LandingPaymentModal from "../components/LandingPaymentModal";
import { TickerInvestimentos } from "../components/TickerInvestimentos";
import { MonyxxStats } from "../components/MonyxxStats";
import { HeroInteractiveCards } from "../components/HeroInteractiveCards";
import TesteHeroRotativo from "./TesteHeroRotativo";
import { Link } from "react-router-dom";
import {
  MessageCircle,
  BarChart3,
  Wallet,
  Bell,
  ShieldCheck,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  Star,
  Target,
  CreditCard,
  PiggyBank,
  FileText,
  Car,
  ShoppingCart,
  Users,
  LineChart,
  Send,
  Mic,
  Image as ImageIcon,
  Lock,
  Clock,
  Smartphone,
  Zap,
  PlayCircle,
  Quote,
  ChevronRight,
  Menu,
  X,
  Instagram,
  Facebook,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------------- NAVBAR ---------------- */
const Navbar = ({ onCta }) => {
  const [open, setOpen] = useState(false);
  const links = [
    { href: "#como-funciona", label: "Como Funciona" },
    { href: "#planos", label: "Planos" },
    { href: "#faq", label: "FAQ" },
  ];
  return (
    <header className="sticky top-0 z-40 backdrop-blur-lg bg-[#0A1A2E]/80 border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-6">
        <a href="#" className="flex items-center gap-2 shrink-0" data-testid="logo-home">
          <img
            src="/monyx-bot-hero.png"
            alt="Monyxx Bot"
            className="h-14 w-14 object-contain select-none drop-shadow-[0_0_12px_rgba(16,185,129,0.5)]"
            data-testid="logo-bot"
          />
          <div className="flex flex-col leading-none">
            <span className="text-2xl md:text-[28px] font-display font-extrabold tracking-tight leading-none">
              <span className="text-emerald-400">M</span><span className="text-white">ony</span><span className="text-blue-400">xx</span>
            </span>
            <span className="text-[9px] tracking-[0.35em] font-bold text-emerald-400/80 mt-0.5 ml-[2px]">Finance</span>
          </div>
        </a>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-slate-300 hover:text-emerald-400 transition"
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <Link to="/app" data-testid="nav-access-app">
            <Button variant="outline" className="rounded-full h-10 px-4 border-slate-700 bg-transparent text-white hover:bg-slate-800 hover:border-emerald-500/50 hover:text-emerald-400 text-sm font-semibold">
              Acessar Sistema
            </Button>
          </Link>
          <Button
            onClick={onCta}
            className="monyx-btn-primary rounded-full px-5 h-10 font-semibold"
            data-testid="nav-cta-btn"
          >
            Quero meu acesso <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
        {/* CTA visível direto no header mobile */}
        <Button
          onClick={onCta}
          className="md:hidden monyx-btn-primary rounded-full px-4 h-9 text-sm font-bold shrink-0"
          data-testid="nav-mobile-cta-header"
        >
          Quero acesso
        </Button>
        <button
          className="md:hidden p-2 rounded-lg hover:bg-slate-800 text-white shrink-0"
          onClick={() => setOpen((s) => !s)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-slate-800 bg-[#0A1A2E]">
          <div className="px-5 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="py-1 text-slate-200 font-medium">
                {l.label}
              </a>
            ))}
            <Link to="/app" onClick={() => setOpen(false)} data-testid="nav-mobile-access-app">
              <Button variant="outline" className="w-full rounded-full mt-2 border-slate-700 bg-transparent text-white">
                Acessar Sistema
              </Button>
            </Link>
            <Button onClick={() => { setOpen(false); onCta(); }} className="monyx-btn-primary rounded-full mt-2" data-testid="nav-mobile-cta">
              Quero meu acesso
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

/* ---------------- HERO BANNER (top) ---------------- */
const HeroBanner = () => (
  <section className="relative bg-[#0A1A2E] border-b border-slate-800 lg:min-h-[calc(100vh-64px)] flex flex-col justify-center py-10 lg:py-0" data-testid="hero-banner">
    <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />
    <div className="relative w-full max-w-7xl mx-auto px-5 md:px-8 py-8 lg:py-12">
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12 px-6 md:px-10">
          {/* Left side: hero image */}
          <div className="flex-1 w-full flex justify-center items-center relative">
            <div
              className="relative w-full max-w-[550px] md:max-w-[650px] lg:max-w-[750px] xl:max-w-[900px] mx-auto lg:-ml-12 xl:-ml-16 lg:scale-[1.15] xl:scale-[1.25]"
              style={{ maskImage: "radial-gradient(ellipse at 50% 50%, black 72%, transparent 100%)", WebkitMaskImage: "radial-gradient(ellipse at 50% 50%, black 72%, transparent 100%)" }}
            >
              <img
                src="/monyx_hero_banner_v16.png"
                alt="Monyxx — Robô assistente, IA e Dashboard"
                className="w-full h-auto object-contain select-none"
                data-testid="hero-banner-img"
                loading="eager"
              />
            </div>
          </div>

          {/* Right side: text + CTAs */}
          <div className="flex-1 w-full flex flex-col justify-center items-center lg:items-start text-center lg:text-left pl-6 lg:pl-10">
            <span className="pill" data-testid="banner-pill">
              <Sparkles className="h-3.5 w-3.5" /> IA por Voz + Painel completo
            </span>
            <h1 className="mt-6 text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-display font-bold leading-[1.1] text-white">
              Quem controla o dinheiro<br />
              controla o <span className="monyx-grad-text">futuro.</span><br />
              O Monyxx é seu <span className="monyx-grad-text">controle.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-300 max-w-xl">
              Registre seus gastos por voz diretamente no painel, ou digitando — em segundos.
              O Monyxx transforma cada lançamento numa decisão financeira inteligente.
            </p>

            {/* 3 formas de uso */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
              <div className="flex flex-col items-center lg:items-start gap-2 p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
                <div className="h-10 w-10 rounded-lg bg-emerald-500/15 border border-emerald-500/30 grid place-items-center">
                  <MessageCircle className="h-5 w-5 text-emerald-400" />
                </div>
                <span className="text-sm font-semibold text-white">Voz e Texto</span>
                <span className="text-xs text-slate-400 leading-tight text-center lg:text-left">Envie uma mensagem e pronto</span>
              </div>
              <div className="flex flex-col items-center lg:items-start gap-2 p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
                <div className="h-10 w-10 rounded-lg bg-blue-500/15 border border-blue-500/30 grid place-items-center">
                  <Mic className="h-5 w-5 text-blue-400" />
                </div>
                <span className="text-sm font-semibold text-white">Voz</span>
                <span className="text-xs text-slate-400 leading-tight text-center lg:text-left">Fale e o Monyxx registra</span>
              </div>
              <div className="flex flex-col items-center lg:items-start gap-2 p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
                <div className="h-10 w-10 rounded-lg bg-emerald-500/15 border border-emerald-500/30 grid place-items-center">
                  <Smartphone className="h-5 w-5 text-emerald-400" />
                </div>
                <span className="text-sm font-semibold text-white">Painel</span>
                <span className="text-xs text-slate-400 leading-tight text-center lg:text-left">Acesse e lance manualmente</span>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap gap-4 justify-center lg:justify-start">
              <Link to="/app" data-testid="banner-cta-primary">
                <Button className="monyx-btn-primary rounded-full h-14 px-8 text-lg font-semibold">
                  Começar agora <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="#como-funciona">
                <Button
                  variant="outline"
                  className="rounded-full h-14 px-8 border-slate-700 bg-transparent text-white hover:bg-slate-800 hover:border-emerald-500/50 hover:text-emerald-400 text-lg font-semibold"
                  data-testid="banner-cta-secondary"
                >
                  <PlayCircle className="mr-2 h-6 w-6 text-emerald-400" /> Como funciona
                </Button>
              </a>
            </div>
          </div>
        </div>
    </div>
  </section>
);

/* ---------------- HERO ---------------- */
const Hero = ({ onCta }) => (
  <section className="relative overflow-hidden">
    <div className="absolute inset-0 grid-pattern opacity-70" />
    <span className="glow-green -top-24 -left-24" />
    <span className="glow-blue -bottom-24 right-0" />

    <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-14 md:pt-24 pb-16 md:pb-24 grid lg:grid-cols-2 gap-12 items-center">
      <div className="reveal">
        <span className="pill" data-testid="hero-pill">
          <Sparkles className="h-3.5 w-3.5" /> Nova era da gestão financeira pessoal
        </span>
        <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-[1.05]">
          Sua vida financeira{" "}
          <span className="monyx-grad-text">em piloto automático</span>.
        </h1>
        <p className="mt-5 text-base md:text-lg text-slate-300 max-w-xl">
          Pare de ser refém de planilhas. O <b>Monyxx</b> transforma cada áudio seu no aplicativo
          em inteligência financeira real — lança, categoriza, projeta e te avisa na hora certa.
          Zero digitação chata. Zero cálculo mental. Total clareza.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Button
            onClick={onCta}
            className="monyx-btn-primary text-white rounded-full h-12 px-7 text-base font-semibold"
            data-testid="hero-cta-primary"
          >
            Quero meu acesso <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <a href="#como-funciona">
            <Button
              variant="outline"
              className="rounded-full h-12 px-6 border-slate-300 hover:border-emerald-400 hover:text-emerald-700 text-base font-semibold"
              data-testid="hero-cta-secondary"
            >
              <PlayCircle className="mr-2 h-5 w-5 text-emerald-400" /> Ver Demo
            </Button>
          </a>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-5 text-sm text-slate-300">
          <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Segurança bancária</div>
          <div className="flex items-center gap-2"><Zap className="h-4 w-4 text-blue-600" /> Velocidade extrema</div>
          <div className="flex items-center gap-2"><Star className="h-4 w-4 text-amber-500" /> 4.9/5 · 2.300+ usuários</div>
        </div>
      </div>

      {/* Visual: real Monyxx dashboard */}
      <div className="relative reveal" style={{ animationDelay: "120ms" }}>
        <div className="absolute -inset-8 -z-10 rounded-[40px] bg-gradient-to-br from-emerald-500/10 via-transparent to-blue-500/10 blur-3xl" />
        <div className="relative">
          <img
            src="https://monyx.web.app/hero_mockup.png?v=30"
            alt="Monyxx SaaS Dashboard"
            className="w-full rounded-2xl shadow-2xl shadow-emerald-500/10 ring-1 ring-white/5"
            data-testid="hero-dashboard-img"
            loading="eager"
          />
        </div>
      </div>
    </div>

    {/* Logos bar */}
    <div className="relative max-w-7xl mx-auto px-5 md:px-8 pb-10">
      <div className="text-xs uppercase tracking-widest text-slate-400 mb-4">Tecnologias e segurança</div>
      <div className="flex flex-wrap items-center gap-8 opacity-80">
        {["Web Speech API", "OpenAI", "Stripe", "Hotmart", "Cloudflare", "PIX"].map((b) => (
          <div key={b} className="text-sm font-semibold text-slate-400">{b}</div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- PORTO SEGURO (3 PILARES) ---------------- */
const PortoSeguro = () => {
  const pillars = [
    {
      icon: <Zap className="h-6 w-6" />,
      t: "Lançamento em 5 segundos",
      d: "Você manda, a IA registra. Nada de menus, abas ou formulários — só uma conversa natural como com um amigo.",
      tone: "green",
    },
    {
      icon: <ShieldCheck className="h-6 w-6" />,
      t: "Seus dados, só seus",
      d: "Arquitetura com criptografia nativa, servidores protegidos e compliance de nível bancário. Sua privacidade é inegociável.",
      tone: "blue",
    },
    {
      icon: <LineChart className="h-6 w-6" />,
      t: "Inteligência que antecipa",
      d: "Mais do que mostrar o passado, o Monyxx projeta o futuro. Alertas, tendências e recomendações baseadas no seu padrão real.",
      tone: "green",
    },
  ];
  return (
    <section className="py-20 md:py-28 bg-[#0A1A2E]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-2">
            <span className="pill-blue pill">POR DENTRO DO MONYXX</span>
            <h2 className="mt-4 text-3xl md:text-5xl font-display font-bold leading-tight">
              Um copiloto, não <span className="monyx-grad-text">só uma planilha</span>
            </h2>
            <p className="mt-4 text-slate-300 text-lg">
              O Monyxx não é só um registrador de gastos — é um copiloto. Cruza seu histórico, identifica tendências e entrega contexto antes de você precisar pedir.
            </p>
            <blockquote className="mt-6 border-l-4 border-emerald-500 pl-4 text-slate-200 italic">
              “Dinheiro organizado não é sobre cortar tudo. É sobre saber exatamente o que sustenta a vida que você quer viver.”
            </blockquote>
          </div>
          <div className="lg:col-span-3 grid md:grid-cols-3 gap-4">
            {pillars.map((p) => (
              <div
                key={p.t}
                className="card-elev rounded-2xl p-6 hover:-translate-y-1 transition"
                data-testid={`pilar-${p.t.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div
                  className={`h-12 w-12 rounded-xl grid place-items-center text-white shadow-sm ${
                    p.tone === "green"
                      ? "bg-gradient-to-br from-emerald-500 to-emerald-600"
                      : "bg-gradient-to-br from-blue-600 to-blue-700"
                  }`}
                >
                  {p.icon}
                </div>
                <h3 className="mt-4 text-lg font-semibold">{p.t}</h3>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ label, value, tone }) => {
  const toneMap = {
    green: "text-emerald-400 bg-emerald-500/10 border border-emerald-500/20",
    blue: "text-blue-400 bg-blue-500/10 border border-blue-500/20",
    ink: "text-white bg-[#0D2137]",
  };
  return (
    <div className={`rounded-lg p-3 ${toneMap[tone]}`}>
      <div className="text-[11px] font-semibold opacity-80">{label}</div>
      <div className="text-base md:text-lg font-bold">{value}</div>
    </div>
  );
};

const Bubble = ({ children, me }) => (
  <div className={`flex ${me ? "justify-end" : "justify-start"}`}>
    <div
      className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${
        me ? "bg-emerald-500 text-slate-950 rounded-br-sm" : "bg-slate-800 text-white rounded-bl-sm"
      }`}
    >
      {children}
    </div>
  </div>
);

/* ---------------- HOW IT WORKS ---------------- */
const HowItWorks = () => {
  const steps = [
    { n: 1, icon: <MessageCircle className="h-5 w-5" />, title: "Converse naturalmente", desc: "Manda um \"gastei 80 no almoço\" ou um áudio rápido. Nossa IA entende gírias, abreviações e contexto." },
    { n: 2, icon: <Sparkles className="h-5 w-5" />, title: "A IA faz o trabalho e organiza tudo", desc: "Classifica, soma, cruza com seu histórico e já atualiza o painel. Se ficou com dúvida, pergunta — não chuta." },
    { n: 3, icon: <LineChart className="h-5 w-5" />, title: "Receba insights acionáveis", desc: "Não é só gráfico bonito. É alerta quando o padrão muda, projeção do fim do mês e recomendação do que cortar." },
  ];
  return (
    <section id="como-funciona" className="py-20 md:py-28 bg-[#0D2137]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="pill-blue pill">ASSISTENTE 24/7</span>
          <h2 className="mt-4 text-3xl md:text-5xl font-display font-bold">
            Em <span className="monyx-grad-text">3 conversas</span> o Monyxx já conhece sua rotina
          </h2>
          <p className="mt-4 text-slate-300">
            Nada de preencher formulário, importar extrato ou configurar categoria. Você fala — o Monyxx aprende.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <Card key={s.n} className="p-7 card-elev rounded-2xl relative overflow-hidden">
              <div className={`absolute -right-6 -top-6 h-24 w-24 rounded-full blur-md opacity-60 ${
                i === 0 ? "bg-gradient-to-br from-emerald-500 to-emerald-700" :
                i === 1 ? "bg-gradient-to-br from-emerald-500 to-blue-600" :
                "bg-gradient-to-br from-blue-500 to-blue-700"
              }`} />
              <div className="relative">
                <div className="h-11 w-11 rounded-xl bg-slate-800 border border-emerald-500/30 text-emerald-400 grid place-items-center shadow-sm">
                  {s.icon}
                </div>
                <div className="mt-5 flex items-center gap-2">
                  <span className="text-4xl font-display font-bold text-slate-200">0{s.n}</span>
                  <h3 className="text-xl font-semibold">{s.title}</h3>
                </div>
                <p className="mt-2 text-slate-300 text-sm leading-relaxed">{s.desc}</p>
                {i < steps.length - 1 && (
                  <ChevronRight className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 text-slate-300" />
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- PAINEL + IA ---------------- */
const PainelSection = () => (
  <section className="py-20 md:py-28">
    <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
      <div>
        <span className="pill">PAINEL COMPLETO</span>
        <h2 className="mt-4 text-3xl md:text-5xl font-display font-bold leading-tight">
          Todo o seu dinheiro numa tela só
        </h2>
        <p className="mt-4 text-slate-300 text-lg">
          Sua voz é a porta de entrada. O painel é seu centro de comando. <b>Saldo, fluxo, metas e investimentos</b> atualizados em tempo real, com a profundidade que planilha nenhuma entrega.
        </p>
        <ul className="mt-6 space-y-3">
          {[
            "Saldo consolidado por conta, cartão e carteira",
            "Categorização inteligente que aprende com você",
            "Projeção de onde seu mês vai terminar",
            "Exportação completa em Excel e PDF quando quiser",
          ].map((t) => (
            <li key={t} className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
              <span className="text-slate-200">{t}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="relative">
        <div className="absolute -inset-8 -z-10 rounded-[32px] bg-gradient-to-br from-blue-100 via-white to-emerald-100 blur-2xl opacity-70" />
        <div className="card-elev rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-slate-400">Dashboard Monyxx</div>
              <div className="text-lg font-semibold">Visão geral</div>
            </div>
            <span className="text-[11px] font-semibold px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">+12,4% vs mês passado</span>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3">
            <MiniKpi icon={<Wallet className="h-4 w-4" />} label="Saldo atual" value="R$ 18.412" tone="green" />
            <MiniKpi icon={<CreditCard className="h-4 w-4" />} label="Fatura" value="R$ 2.189" tone="blue" />
            <MiniKpi icon={<PiggyBank className="h-4 w-4" />} label="Poupança" value="R$ 9.300" tone="green" />
            <MiniKpi icon={<Target className="h-4 w-4" />} label="Meta viagem" value="76%" tone="blue" />
          </div>

          <div className="mt-5 rounded-xl border border-slate-800 p-4">
            <div className="text-xs font-semibold text-slate-400 mb-3">Últimas transações</div>
            {[
              { t: "Supermercado Dia", c: "Alimentação", v: "-R$ 189,90", pos: false },
              { t: "Salário", c: "Receita", v: "+R$ 6.500,00", pos: true },
              { t: "Uber", c: "Transporte", v: "-R$ 45,00", pos: false },
              { t: "Netflix", c: "Lazer", v: "-R$ 39,90", pos: false },
            ].map((r) => (
              <div key={r.t} className="flex items-center justify-between py-2 border-b last:border-b-0 border-slate-800">
                <div>
                  <div className="text-sm font-medium">{r.t}</div>
                  <div className="text-[11px] text-slate-400">{r.c}</div>
                </div>
                <div className={`text-sm font-semibold ${r.pos ? "text-emerald-400" : "text-white"}`}>{r.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const MiniKpi = ({ icon, label, value, tone }) => (
  <div className={`rounded-xl p-3 border ${tone === "green" ? "bg-emerald-500/5 border-emerald-500/20" : "bg-blue-500/5 border-blue-500/20"}`}>
    <div className={`flex items-center gap-2 text-xs font-semibold ${tone === "green" ? "text-emerald-400" : "text-blue-400"}`}>{icon} {label}</div>
    <div className="mt-1.5 text-lg font-bold text-white">{value}</div>
  </div>
);

/* ---------------- LEMBRETES ---------------- */
const RemindersSection = () => (
  <section className="pt-0 pb-20 md:pt-0 md:pb-24 bg-[#0A1A2E] relative z-10">
    <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
      <div className="order-2 lg:order-1 relative">
        <div className="flex flex-col items-center gap-6 md:flex-row md:gap-6">
          <img
            src="/monyxx_robot_cell.png"
            alt="Monyxx Bot"
            className="h-[280px] md:h-[340px] w-auto drop-shadow-[0_20px_40px_rgba(16,185,129,0.35)] float select-none pointer-events-none shrink-0 rounded-[32px] mix-blend-lighten"
            data-testid="reminder-bot-img"
          />
          <div className="card-elev rounded-2xl p-5 w-full max-w-sm">
            <div className="flex items-center gap-3 pb-3 border-b border-slate-800">
              <div className="h-9 w-9 rounded-full bg-blue-600 text-white grid place-items-center shrink-0">
                <Bell className="h-4 w-4" />
              </div>
              <div>
                <div className="text-sm font-semibold">Lembrete de contas</div>
                <div className="text-[11px] text-slate-400">Hoje, 08:00</div>
              </div>
            </div>
            <div className="mt-4 space-y-3">
              {[
                { t: "Cartão Visa · vence amanhã", v: "R$ 1.240,00" },
                { t: "Aluguel · vence em 3 dias", v: "R$ 1.850,00" },
                { t: "Internet · hoje", v: "R$ 119,90" },
              ].map((r) => (
                <div key={r.t} className="flex items-center justify-between p-3 rounded-xl bg-[#0D2137] gap-2">
                  <div className="text-sm min-w-0 flex-1">{r.t}</div>
                  <div className="text-sm font-semibold shrink-0">{r.v}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
              <Smartphone className="h-4 w-4" /> Registrado via IA
            </div>
          </div>
        </div>
      </div>

      <div className="order-1 lg:order-2">
        <span className="pill-blue pill">MONYXX AVISA</span>
        <h2 className="mt-4 text-3xl md:text-5xl font-display font-bold leading-tight">
          Seu <span className="monyx-grad-text">secretário particular</span> que nunca esquece
        </h2>
        <p className="mt-4 text-slate-300 text-lg">
          O Monyxx estuda suas contas fixas, seus recebimentos e sua rotina. Todo dia você recebe um briefing curto no app com o que importa hoje — e só isso. Sem spam, sem notificação inútil.
        </p>
        <ul className="mt-6 space-y-3">
          {[
            "Resumo matinal com o que vence e o que entra no dia",
            "Alerta preventivo 1 dia antes de cada vencimento",
            "Aviso quando um gasto fugir do seu padrão mensal",
          ].map((t) => (
            <li key={t} className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-blue-400 mt-0.5 shrink-0" />
              <span className="text-slate-200">{t}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

/* ---------------- FEATURES GRID ---------------- */
const Features = () => {
  const items = [
    { icon: <BarChart3 />, t: "Dashboard com Gráficos" },
    { icon: <MessageCircle />, t: "Agente IA (texto e áudio)" },
    { icon: <Wallet />, t: "Controle de Receitas e Despesas" },
    { icon: <PlayCircle />, t: "Tutoriais em vídeo" },
    { icon: <Sparkles />, t: "Categorias personalizadas" },
    { icon: <CreditCard />, t: "Controle de Cartões" },
    { icon: <FileText />, t: "Faturas e Parcelas" },
    { icon: <LineChart />, t: "Controle de Investimentos" },
    { icon: <ShieldCheck />, t: "Gestão de Dívidas" },
    { icon: <Clock />, t: "Contas a pagar e receber" },
    { icon: <Target />, t: "Metas financeiras" },
    { icon: <Bell />, t: "Lembretes Automáticos" },
    { icon: <ShoppingCart />, t: "Lista de Supermercado" },
    { icon: <ShoppingCart />, t: "Planejamento de Compras" },
    { icon: <Car />, t: "Veículos e Manutenções" },
    { icon: <FileText />, t: "Relatórios gerais" },
    { icon: <FileText />, t: "Exportação em Excel" },
    { icon: <LineChart />, t: "Projeção Financeira" },
    { icon: <Smartphone />, t: "App instalável (PWA)" },
    { icon: <Zap />, t: "E muito mais…" },
  ];
  return (
    <section id="funcionalidades" className="py-20 md:py-28 bg-[#0A1A2E]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="pill">FUNCIONALIDADES</span>
          <h2 className="mt-4 text-3xl md:text-5xl font-display font-bold">
            Tudo que você precisa em <span className="monyx-grad-text">um só lugar</span>
          </h2>
          <p className="mt-4 text-slate-300">Conheça todas as funcionalidades disponíveis no Monyxx — todas inclusas em qualquer plano.</p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {items.map((it) => (
            <div
              key={it.t}
              className="group rounded-xl p-4 bg-[#0D2137] border border-slate-800 hover:border-emerald-500/50 hover:bg-slate-800/60 transition flex items-center gap-3"
              data-testid={`feature-${it.t.toLowerCase().replace(/\s|\(|\)|,/g, "-")}`}
            >
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-emerald-500 to-blue-600 text-white grid place-items-center shadow-sm group-hover:scale-105 transition">
                {React.cloneElement(it.icon, { className: "h-5 w-5" })}
              </div>
              <div className="text-sm font-medium text-white">{it.t}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- IDEAL PARA VOCÊ ---------------- */
const IdealFor = () => {
  const items = [
    { icon: <Wallet />, t: "Descobrir o ralo invisível do seu salário", d: "A IA identifica padrões e aponta onde o dinheiro está escoando sem você perceber." },
    { icon: <Sparkles />, t: "Desinstalar o app de planilha da sua vida", d: "Troca 4 abas de Excel por uma conversa. Tudo sincronizado, zero retrabalho." },
    { icon: <ShieldCheck />, t: "Quitar dívidas com estratégia, não com culpa", d: "Simulamos cenários de pagamento e você enxerga a data exata da sua liberdade." },
    { icon: <PiggyBank />, t: "Guardar mais sem cortar o que ama", d: "Metas realistas com ajuste automático conforme o mês evolui — sem crise, sem frustração." },
    { icon: <Clock />, t: "Controlar tudo em 30 segundos por dia", d: "Mandou a mensagem, acabou. O Monyxx cuida do resto enquanto você vive sua vida." },
    { icon: <LineChart />, t: "Tomar decisões com números, não com achismo", d: "Antes de parcelar, de investir ou de cortar, consulte seu painel. Ele te responde na hora." },
  ];
  return (
    <section className="py-20 md:py-28 bg-[#0D2137]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-display font-bold">
            O Monyxx é pra você que já cansou de…
          </h2>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((it) => (
            <Card key={it.t} className="p-6 card-elev rounded-2xl hover:-translate-y-0.5 transition">
              <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-emerald-500 to-blue-600 text-white grid place-items-center shadow-sm">
                {React.cloneElement(it.icon, { className: "h-5 w-5" })}
              </div>
              <h3 className="mt-4 text-lg font-semibold leading-snug">{it.t}</h3>
              <p className="mt-2 text-sm text-slate-300">{it.d}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- TESTIMONIALS ---------------- */
const Testimonials = () => {
  const list = [
    { n: "Felipe Andrade", d: "12/11/2025", t: "Finalmente entendi meu fluxo de caixa", q: "Em duas semanas usando o Monyxx eu já vi onde estavam os vazamentos do meu mês. Coisa que planilha nenhuma me mostrou em anos." },
    { n: "Tatiane Borges", d: "29/10/2025", t: "O áudio inteligente é viciante", q: "Eu falo enquanto dirijo e a IA registra tudo certinho. Nunca foi tão fácil acompanhar gastos." },
    { n: "Renato Albuquerque", d: "07/09/2025", t: "Saí do vermelho em 4 meses", q: "Comecei a ver as parcelas e prazos numa tela só. Bastou isso pra eu reorganizar a vida e quitar o cartão." },
    { n: "Camila Nunes", d: "18/08/2025", t: "Suporte responde rápido", q: "Tive uma dúvida no domingo à noite e fui atendida em minutos. Ferramenta boa e equipe presente." },
    { n: "Lucas Moretti", d: "02/07/2025", t: "Organização impecável", q: "Parei de anotar gastos no papel. Agora registro tudo pelo celular em 5 segundos e não perco mais nenhuma conta." },
    { n: "Aline Rocha", d: "15/06/2025", t: "Os lembretes me salvam", q: "Pago contas em dia agora. O resumo da manhã no app virou parte da minha rotina." },
  ];
  return (
    <section className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="pill-blue pill">DEPOIMENTOS</span>
          <h2 className="mt-4 text-3xl md:text-5xl font-display font-bold">
            O que dizem nossos <span className="monyx-grad-text">usuários</span>
          </h2>
          <p className="mt-4 text-slate-300">Uma nova geração de pessoas está deixando de depender de planilha — e vivendo com mais paz financeira.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map((r) => (
            <Card key={r.n} className="p-6 card-elev rounded-2xl">
              <Quote className="h-5 w-5 text-emerald-500" />
              <h3 className="mt-3 font-semibold">{r.t}</h3>
              <p className="mt-2 text-sm text-slate-300">“{r.q}”</p>
              <div className="mt-4 flex items-center justify-between text-xs">
                <div>
                  <div className="font-semibold text-white">{r.n}</div>
                  <div className="text-slate-400">Cliente verificado · {r.d}</div>
                </div>
                <div className="flex gap-0.5 text-amber-500">
                  {["s1", "s2", "s3", "s4", "s5"].map((sk) => <Star key={sk} className="h-3.5 w-3.5 fill-current" />)}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- PRICING ---------------- */
const PlanCard = ({ p, onSelect }) => {
  const isDark    = p.tone === "dark";
  const isAccent  = p.tone === "accent";
  const isPremium = p.tone === "premium";

  const cardStyle = isDark ? {
    background: "#0f1117",
    border: "1px solid #2a2a3a",
  } : isAccent ? {
    background: "linear-gradient(135deg, #0d3320 0%, #0f1117 100%)",
    border: "2px solid #00c853",
    boxShadow: "0 0 20px rgba(0,200,83,0.3)",
  } : {
    background: "linear-gradient(135deg, #0a1628 0%, #0d1f3c 100%)",
    border: "2px solid #ffd700",
    boxShadow: "0 0 25px rgba(255,215,0,0.25)",
  };

  const checkColor = isDark ? "#9ca3af" : isAccent ? "#00c853" : "#ffd700";

  const btnStyle = {
    background: "#00c853", color: "#000000", fontWeight: "700",
    boxShadow: "0 4px 15px rgba(0,200,83,0.4)",
  };

  const topBadgeStyle = isPremium
    ? { background: "#3b82f6", color: "#fff", fontWeight: "700" }
    : { background: "#00c853", color: "#000", fontWeight: "700" };

  return (
    <Card
      className="relative p-7 rounded-3xl overflow-hidden transition flex flex-col h-full text-white"
      style={cardStyle}
      data-testid={`plan-${p.id}`}
    >
      {/* Linha decorativa topo — apenas Premium */}
      {isPremium && (
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 4,
          background: "linear-gradient(90deg, #1a56db, #60a5fa)",
        }} />
      )}

      {/* Selo topo */}
      {p.topBadge && (
        <div
          className="absolute top-4 right-4 text-[11px] px-2.5 py-1 rounded-full"
          style={topBadgeStyle}
        >
          {p.topBadge}
        </div>
      )}

      {/* Título */}
      <h3
        className="font-display text-2xl font-bold pr-24"
        style={isPremium ? {
          background: "linear-gradient(90deg, #ffd700, #ffaa00)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
        } : {}}
      >
        {p.name}
      </h3>
      <p className="text-sm mt-1 text-slate-400">{p.desc}</p>

      {/* Preço riscado */}
      {p.old && (
        <div className="mt-5 text-sm line-through text-slate-500">De {p.old}</div>
      )}

      {/* Preço principal */}
      <div className={`flex items-end gap-2 ${p.old ? "mt-1" : "mt-6"}`}>
        <span className="text-5xl font-display font-bold text-white">{p.price}</span>
        <span className="pb-2 text-sm text-slate-400">{p.per}</span>
      </div>
      {p.inst && <div className="text-xs text-slate-400">{p.inst}</div>}

      {/* Badge de economia */}
      {p.savingsBadge && (
        <div
          className="mt-3 text-xs font-semibold px-3 py-1.5 rounded-full inline-block"
          style={isPremium
            ? { background: "#1e3a5f", border: "1px solid #ffd700", color: "#ffd700" }
            : { background: "#00c853", color: "#000", fontWeight: "700" }}
        >
          {p.savingsBadge}
        </div>
      )}
      {p.equiv && (
        <div
          className="text-xs mt-1 font-medium"
          style={{ color: isPremium ? "#ffd700" : "#00c853" }}
        >
          {p.equiv}
        </div>
      )}

      {/* Features */}
      <ul className="mt-6 space-y-2.5">
        {p.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-slate-200">
            {f.startsWith("Tudo do Plano")
              ? <ArrowRight style={{ color: checkColor }} className="h-4 w-4 mt-0.5 shrink-0" />
              : <CheckCircle2 style={{ color: checkColor }} className="h-4 w-4 mt-0.5 shrink-0" />}
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <div className="mt-auto pt-7 w-full">
        <Button
          className="w-full h-12 rounded-full text-base transition hover:opacity-90 active:scale-95"
          style={btnStyle}
          data-testid={`plan-cta-${p.id}`}
          onClick={() => onSelect(p.id)}
        >
          {p.cta}
        </Button>
        <div className="mt-5 flex items-center justify-center gap-2 text-xs text-slate-500">
          <Lock className="h-3.5 w-3.5" /> {p.footer}
        </div>
      </div>
    </Card>
  );
};

const Pricing = () => {
  const [modalPlano, setModalPlano] = useState(null);
  const plans = [
    {
      id: "mensal",
      name: "Plano Mensal",
      desc: "Cobrança mensal recorrente",
      old: null,
      price: "R$ 14,90",
      per: "/mês",
      inst: "",
      savingsBadge: null,
      equiv: null,
      topBadge: null,
      features: [
        "Painel Principal completo",
        "Controle de Entradas",
        "Gastos Fixos e Variáveis",
        "Cartões de Crédito",
        "Dashboard Anual",
        "Planos e Metas",
        "Lista de Compras",
        "Categorias personalizadas",
        "📈 Carteira de Ações",
        "🏢 Carteira de FIIs",
        "🪙 Carteira Criptomoedas",
        "💰 Renda Fixa",
        "🔔 Alertas automáticos",
        "🔄 Atualizações futuras",
      ],
      cta: "Eu quero o Mensal →",
      footer: "Pagamento 100% seguro · Hotmart / Mercado Pago / PIX",
      link: process.env.REACT_APP_HOTMART_MENSAL || "https://pay.hotmart.com/U106046685M",
      tone: "dark",
    },
    {
      id: "anual-essencial",
      name: "Plano Anual Essencial",
      desc: "Pagamento único anual",
      old: "R$ 178,80",
      price: "R$ 99,90",
      per: "/ano",
      inst: "ou 6x de R$ 16,65",
      savingsBadge: "🔥 Economize R$ 78,90 (44% OFF)",
      equiv: "R$ 8,32/mês",
      topBadge: "44% OFF",
      features: [
        "Painel Principal completo",
        "Controle de Entradas",
        "Gastos Fixos e Variáveis",
        "Cartões de Crédito",
        "Dashboard Anual",
        "Planos e Metas",
        "Lista de Compras",
        "Categorias personalizadas",
        "📈 Carteira de Ações",
        "🏢 Carteira de FIIs",
        "🪙 Carteira Criptomoedas",
        "💰 Renda Fixa",
        "🔔 Alertas automáticos",
        "🔄 Atualizações futuras",
      ],
      cta: "Eu quero o Essencial →",
      footer: "Pagamento 100% seguro · Hotmart / Mercado Pago / PIX",
      link: process.env.REACT_APP_HOTMART_ANUAL_ESSENCIAL || "https://pay.hotmart.com/C106046581R",
      tone: "accent",
    },
    {
      id: "anual-premium",
      name: "Plano Anual Premium",
      desc: "Pagamento único anual",
      old: "R$ 199,80",
      price: "R$ 139,90",
      per: "/ano",
      inst: "ou 6x de R$ 23,31",
      savingsBadge: "🔥 Por apenas R$ 3,33/mês a mais, ganhe Família!",
      equiv: "R$ 11,66/mês",
      topBadge: "💎 MAIS VANTAJOSO",
      features: [
        "Tudo do Plano Anual Essencial",
        "👨‍👩‍👧 Módulo Família completo",
        "💑 Cônjuge acessa gratuitamente",
        "🤝 2 contas conectadas",
        "📊 Visão financeira do casal",
        "🔄 Todas as atualizações futuras",
      ],
      cta: "Eu quero o Premium →",
      footer: "Pagamento 100% seguro · Hotmart / Mercado Pago / PIX",
      link: process.env.REACT_APP_HOTMART_ANUAL_PREMIUM || "https://pay.hotmart.com/K106046660U",
      tone: "premium",
    },
  ];

  return (
    <section id="planos" className="py-20 md:py-28 bg-[#0A1A2E]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        
        {/* SEÇÃO DESTAQUE MÓDULO FAMÍLIA */}
        <div className="max-w-4xl mx-auto mb-16 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-bold uppercase tracking-wider mb-4">
            <Users className="h-4 w-4" /> Exclusivo do Plano Anual Premium
          </div>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
            👫 Gerencie as finanças do casal juntos
          </h2>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto mb-8">
            Com o Módulo Família você e seu cônjuge têm acesso completo com um único plano
          </p>
          
          <div className="grid md:grid-cols-3 gap-4 text-left">
            <Card className="bg-slate-800/50 border-slate-700/50 p-5">
              <div className="h-10 w-10 rounded-lg bg-blue-500/20 text-blue-400 flex items-center justify-center mb-4">
                <Lock className="h-5 w-5" />
              </div>
              <h4 className="font-bold text-white mb-2">👤 Login individual</h4>
              <p className="text-sm text-slate-400">Cada um acessa com seu próprio usuário e senha de forma independente.</p>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700/50 p-5">
              <div className="h-10 w-10 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center mb-4">
                <BarChart3 className="h-5 w-5" />
              </div>
              <h4 className="font-bold text-white mb-2">📊 Painel consolidado</h4>
              <p className="text-sm text-slate-400">Visualizem juntos todas as receitas, gastos e investimentos do casal em tempo real.</p>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700/50 p-5">
              <div className="h-10 w-10 rounded-lg bg-purple-500/20 text-purple-400 flex items-center justify-center mb-4">
                <Sparkles className="h-5 w-5" />
              </div>
              <h4 className="font-bold text-white mb-2">👫 Cônjuge grátis</h4>
              <p className="text-sm text-slate-400">Seu cônjuge acessa a plataforma sem nenhum custo adicional no Plano Anual Premium.</p>
            </Card>
          </div>
        </div>

        {/* HEADER DOS PLANOS */}
        <div className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="text-3xl md:text-5xl font-display font-bold">
            Escolha o seu plano
          </h2>
          <p className="mt-4 text-slate-300">
            Comece a organizar sua vida financeira hoje
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto items-stretch">
          {plans.map((p) => (
            <PlanCard
              key={p.id}
              p={p}
              onSelect={(id) => setModalPlano(id)}
            />
          ))}
        </div>

        <div className="mt-16 max-w-3xl mx-auto text-center rounded-2xl p-6 bg-emerald-500/5 border border-emerald-500/20">
          <div className="flex items-center justify-center gap-2 text-emerald-400 font-semibold">
            <ShieldCheck className="h-5 w-5" /> Pagamento 100% Seguro
          </div>
          <p className="mt-2 text-sm text-slate-300">
            O Monyxx utiliza as plataformas mais seguras do mercado para processar seu pagamento.
          </p>
        </div>

        <LandingPaymentModal
          isOpen={!!modalPlano}
          onClose={() => setModalPlano(null)}
          plano={modalPlano}
        />
      </div>
    </section>
  );
};

/* ---------------- FAQ ---------------- */
const FAQ = () => {
  const qs = [
    { q: "Qual a diferença real entre o Monyxx e uma planilha de Excel?", a: "Planilha é estática e exige disciplina diária. Monyxx é proativo: ele registra por você, identifica padrões, projeta o fim do mês e te avisa antes dos problemas acontecerem." },
    { q: "Preciso ter conhecimento em finanças pra usar?", a: "Não. Se você manda áudio, tá pronto. A IA é treinada pra entender linguagem do dia a dia — \"comi fora hoje\", \"paguei boleto\", \"recebi freela\". Tudo funciona." },
    { q: "Como vocês garantem a segurança dos meus dados financeiros?", a: "Criptografia ponta-a-ponta nas conversas, servidores protegidos pela infraestrutura Cloudflare, logs auditados e zero compartilhamento com terceiros. Seus dados são seus." },
    { q: "Funciona no celular, computador e tablet?", a: "Sim. Painel web responsivo, PWA instalável (parece app nativo) e acessível em qualquer aparelho que você já usa." },
    { q: "A IA entende áudio, foto de nota fiscal e PIX?", a: "Entende os três. Pode mandar áudio descrevendo o gasto, foto do comprovante/recibo ou comprovante de PIX — a IA extrai e registra tudo sozinha." },
    { q: "Tem algum limite de transações ou de usuários?", a: "Transações ilimitadas. Os planos incluem perfil pessoal + empresarial e você pode compartilhar com cônjuge, sócio ou família sem custo extra." },
    { q: "E se eu experimentar e não gostar?", a: "Garantia incondicional de 7 dias. Pede reembolso no suporte, devolvemos 100% do valor, sem questionário ou burocracia." },
    { q: "Qual plano faz mais sentido pra mim?", a: "Se você quer testar sem compromisso longo, Anual. Se você já tem certeza de que precisa dessa ferramenta pra sempre, Vitalício compensa em menos de 2 anos." },
    { q: "Consigo exportar meus dados?", a: "Sim, exportação em Excel e PDF sempre que quiser. Seus dados ficam sob seu controle, sem vendor lock-in." },
    { q: "Como é o atendimento se eu precisar de ajuda?", a: "Suporte humano via chat em horário comercial, resposta em até 1h útil. Fora do horário, base de conhecimento e tutoriais em vídeo 24h." },
  ];
  return (
    <section id="faq" className="py-20 md:py-28 bg-[#0A1A2E]">
      <div className="max-w-4xl mx-auto px-5 md:px-8">
        <div className="text-center">
          <span className="pill">PERGUNTAS FREQUENTES</span>
          <h2 className="mt-4 text-3xl md:text-5xl font-display font-bold">As respostas antes das suas perguntas</h2>
          <p className="mt-4 text-slate-300">Confira as perguntas mais comuns sobre o Monyxx</p>
        </div>
        <Accordion type="single" collapsible className="mt-10 divide-y divide-slate-800 border border-slate-800/60 rounded-2xl bg-gradient-to-br from-slate-900 via-[#101935]/60 to-slate-950 shadow-lg">
          {qs.map((item, i) => (
            <AccordionItem key={item.q} value={`item-${i}`} className="px-5">
              <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left font-semibold py-5 hover:no-underline text-white text-lg">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-200 pb-5 text-base leading-relaxed">{item.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

/* ---------------- FINAL CTA ---------------- */
const FinalCta = ({ onCta }) => (
  <section className="py-20 md:py-28">
    <div className="max-w-6xl mx-auto px-5 md:px-8">
      <div className="relative overflow-hidden rounded-3xl p-10 md:p-14 bg-gradient-to-br from-emerald-600 via-emerald-700 to-blue-800 text-white">
        <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -left-16 -bottom-16 w-72 h-72 rounded-full bg-emerald-400/20 blur-3xl" />
        <div className="relative max-w-2xl">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold">
            <Sparkles className="h-3.5 w-3.5" /> Comece agora
          </span>
          <h2 className="mt-4 text-3xl md:text-5xl font-display font-bold leading-tight">
            O primeiro passo pra sua tranquilidade financeira começa com uma mensagem
          </h2>
          <p className="mt-4 text-emerald-50">
            Cadastre-se em menos de 60 segundos e veja seu primeiro gasto sendo registrado automaticamente pela IA. Com 7 dias de garantia.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Button onClick={onCta} className="bg-white hover:bg-slate-100 text-emerald-700 h-12 px-7 rounded-full font-semibold" data-testid="final-cta-primary">
              Quero minha paz financeira <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <a href="#planos">
              <Button variant="outline" className="h-12 px-6 rounded-full border-white/40 text-white hover:bg-white/10 hover:text-white font-semibold" data-testid="final-cta-secondary">
                Ver planos
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- MODAL PRIVACIDADE ---------------- */
const PrivacidadeModal = ({ onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
    <div
      className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
      onClick={e => e.stopPropagation()}
    >
      <div className="sticky top-0 bg-slate-900 border-b border-slate-800 px-6 py-4 flex items-center justify-between">
        <span className="font-semibold text-white">Política de Privacidade</span>
        <button onClick={onClose} className="text-slate-400 hover:text-white text-xl leading-none">✕</button>
      </div>
      <div className="px-6 py-6 space-y-6 text-slate-300 text-sm leading-relaxed">
        <p className="text-slate-500 text-xs">Última atualização: outubro de 2026</p>
        <p>O <strong className="text-white">Monyxx</strong> respeita sua privacidade e está comprometido com a proteção dos seus dados pessoais, em conformidade com a <strong className="text-white">Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018)</strong>.</p>

        <section><h3 className="text-white font-semibold mb-2">1. Dados que Coletamos</h3>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li><strong className="text-slate-200">Cadastro:</strong> nome, e-mail e senha (armazenada com hash seguro).</li>
            <li><strong className="text-slate-200">Login social:</strong> nome e e-mail fornecidos pelo Google via Firebase Authentication.</li>
            <li><strong className="text-slate-200">Dados financeiros:</strong> lançamentos que você insere voluntariamente.</li>
            <li><strong className="text-slate-200">Áudio (voz):</strong> processado em tempo real para transcrição, não armazenado permanentemente.</li>
            <li><strong className="text-slate-200">Pagamento:</strong> e-mail e ID de transação. Não armazenamos dados de cartão.</li>
          </ul>
        </section>

        <section><h3 className="text-white font-semibold mb-2">2. Como Usamos seus Dados</h3>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Fornecer e personalizar as funcionalidades do aplicativo.</li>
            <li>Processar pagamentos e ativar seu plano de assinatura.</li>
            <li>Transcrever comandos de voz para criação de lançamentos financeiros.</li>
            <li>Enviar e-mails transacionais (confirmação de cadastro, ativação de plano).</li>
          </ul>
        </section>

        <section><h3 className="text-white font-semibold mb-2">3. Compartilhamento de Dados</h3>
          <p className="mb-2">Não vendemos dados pessoais. Compartilhamos apenas com prestadores essenciais:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li><strong className="text-slate-200">Google Firebase:</strong> autenticação.</li>
            <li><strong className="text-slate-200">Groq:</strong> transcrição de áudio e IA.</li>
            <li><strong className="text-slate-200">Mercado Pago / Hotmart:</strong> pagamentos.</li>
            <li><strong className="text-slate-200">MongoDB Atlas:</strong> armazenamento seguro na nuvem.</li>
          </ul>
        </section>

        <section><h3 className="text-white font-semibold mb-2">4. Segurança</h3>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Comunicação criptografada com HTTPS/TLS.</li>
            <li>Senhas armazenadas com hash bcrypt.</li>
            <li>Sessões protegidas por tokens JWT com validade limitada.</li>
          </ul>
        </section>

        <section><h3 className="text-white font-semibold mb-2">5. Seus Direitos (LGPD)</h3>
          <p className="mb-2">Você pode solicitar acesso, correção, exclusão e portabilidade dos seus dados a qualquer momento.</p>
          <p>Contato: <a href="mailto:suporte@monyxx.com.br" className="text-emerald-400 hover:underline">suporte@monyxx.com.br</a></p>
        </section>

        <section><h3 className="text-white font-semibold mb-2">6. Cookies</h3>
          <p>Utilizamos apenas cookies estritamente necessários para manter sua sessão autenticada. Não usamos cookies de rastreamento ou publicidade.</p>
        </section>

        <div className="pt-4 text-center">
          <button onClick={onClose} className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-sm font-semibold transition-colors">
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
);

/* ---------------- MODAL TERMOS ---------------- */
const TermosModal = ({ onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
    <div
      className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
      onClick={e => e.stopPropagation()}
    >
      <div className="sticky top-0 bg-slate-900 border-b border-slate-800 px-6 py-4 flex items-center justify-between">
        <span className="font-semibold text-white">Termos de Uso</span>
        <button onClick={onClose} className="text-slate-400 hover:text-white text-xl leading-none">✕</button>
      </div>
      <div className="px-6 py-6 space-y-6 text-slate-300 text-sm leading-relaxed">
        <p className="text-slate-500 text-xs">Última atualização: outubro de 2026</p>
        <p>Ao acessar ou utilizar o <strong className="text-white">Monyxx</strong>, você concorda com os termos descritos abaixo. Leia com atenção antes de usar o serviço.</p>

        <section><h3 className="text-white font-semibold mb-2">1. Aceitação dos Termos</h3>
          <p>O uso do Monyxx implica aceitação integral destes Termos de Uso. Caso não concorde com algum ponto, não utilize o serviço.</p>
        </section>

        <section><h3 className="text-white font-semibold mb-2">2. Descrição do Serviço</h3>
          <p>O Monyxx é um aplicativo de gestão financeira pessoal com inteligência artificial que permite registrar lançamentos por voz, acompanhar gastos, investimentos e objetivos financeiros. O serviço é fornecido mediante assinatura e pode ser alterado ou descontinuado a qualquer momento mediante aviso prévio.</p>
        </section>

        <section><h3 className="text-white font-semibold mb-2">3. Cadastro e Conta</h3>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Você é responsável por manter a confidencialidade da sua senha.</li>
            <li>As informações fornecidas no cadastro devem ser verdadeiras e atualizadas.</li>
            <li>É proibido compartilhar o acesso à sua conta com terceiros.</li>
            <li>O Monyxx não se responsabiliza por acessos não autorizados decorrentes de negligência do usuário.</li>
          </ul>
        </section>

        <section><h3 className="text-white font-semibold mb-2">4. Planos e Pagamento</h3>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>O acesso completo ao Monyxx requer um plano pago ativo.</li>
            <li>Os pagamentos são processados por plataformas terceiras (Mercado Pago / Hotmart).</li>
            <li>Planos são cobrados de forma recorrente conforme o período escolhido (mensal ou anual).</li>
            <li>Solicitações de reembolso devem ser feitas em até 7 dias após a compra pelo e-mail <a href="mailto:suporte@monyxx.com.br" className="text-emerald-400 hover:underline">suporte@monyxx.com.br</a>.</li>
          </ul>
        </section>

        <section><h3 className="text-white font-semibold mb-2">5. Uso Adequado</h3>
          <p className="mb-2">É expressamente proibido:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Usar o serviço para fins ilegais ou fraudulentos.</li>
            <li>Tentar acessar dados de outros usuários.</li>
            <li>Realizar engenharia reversa ou copiar o aplicativo.</li>
            <li>Revender, sublicenciar ou redistribuir o acesso ao serviço.</li>
          </ul>
        </section>

        <section><h3 className="text-white font-semibold mb-2">6. Limitação de Responsabilidade</h3>
          <p>O Monyxx é uma ferramenta de organização financeira e não constitui aconselhamento financeiro profissional. O usuário é inteiramente responsável pelas decisões tomadas com base nos dados exibidos no aplicativo. O Monyxx não se responsabiliza por perdas financeiras decorrentes do uso do serviço.</p>
        </section>

        <section><h3 className="text-white font-semibold mb-2">7. Cancelamento</h3>
          <p>Você pode cancelar sua assinatura a qualquer momento. Após o cancelamento, o acesso permanece ativo até o fim do período pago. Os dados da conta ficam disponíveis por 30 dias após o encerramento, podendo ser solicitada a exclusão definitiva pelo suporte.</p>
        </section>

        <section><h3 className="text-white font-semibold mb-2">8. Alterações nos Termos</h3>
          <p>O Monyxx pode atualizar estes termos a qualquer momento. Mudanças relevantes serão comunicadas por e-mail ou aviso no aplicativo. O uso continuado após a notificação implica aceitação da nova versão.</p>
        </section>

        <section><h3 className="text-white font-semibold mb-2">9. Contato</h3>
          <p>Dúvidas sobre estes termos? Entre em contato: <a href="mailto:suporte@monyxx.com.br" className="text-emerald-400 hover:underline">suporte@monyxx.com.br</a></p>
        </section>

        <div className="pt-4 text-center">
          <button onClick={onClose} className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-sm font-semibold transition-colors">
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
);

/* ---------------- FOOTER ---------------- */
const Footer = () => {
  const [privacidadeOpen, setPrivacidadeOpen] = useState(false);
  const [termosOpen, setTermosOpen] = useState(false);
  return (
  <>
  <footer className="border-t border-slate-800 bg-[#0A1A2E]">
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 grid md:grid-cols-4 gap-8">
      <div>
        <div className="flex items-center gap-2">
          <img
            src="/monyx-bot-hero.png"
            alt="Monyxx Bot"
            className="h-12 w-12 object-contain select-none"
          />
          <div className="flex flex-col leading-none">
            <span className="text-2xl font-display font-extrabold tracking-tight">
              <span className="text-emerald-400">M</span><span className="text-white">ony</span><span className="text-blue-400">xx</span>
            </span>
            <span className="text-[9px] tracking-[0.35em] font-bold text-emerald-400/80 mt-0.5 ml-[2px]">Finance</span>
          </div>
        </div>
        <p className="mt-3 text-sm text-slate-300">Seu agente financeiro particular com IA — tudo direto no app.</p>
        <div className="mt-4 flex items-center gap-3">
          <a href="https://www.facebook.com/profile.php?id=61592574148472" target="_blank" rel="noreferrer" className="h-8 w-8 rounded-full bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center text-white hover:-translate-y-1 hover:shadow-[0_4px_12px_rgba(16,185,129,0.3)] transition-all" title="Facebook">
            <Facebook className="h-5 w-5" fill="currentColor" stroke="none" />
          </a>
          <a href="https://instagram.com/monyxxfinance" target="_blank" rel="noreferrer" className="h-8 w-8 rounded-full bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center text-white hover:-translate-y-1 hover:shadow-[0_4px_12px_rgba(16,185,129,0.3)] transition-all" title="Instagram">
            <Instagram className="h-5 w-5" />
          </a>
          <a href="#" target="_blank" rel="noreferrer" className="h-8 w-8 rounded-full bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center text-white hover:-translate-y-1 hover:shadow-[0_4px_12px_rgba(16,185,129,0.3)] transition-all" title="TikTok">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 2.22-1.15 4.39-2.9 5.8-1.76 1.4-4.14 2.11-6.4 1.62-2.3-.5-4.32-1.89-5.46-3.92-1.15-2.06-1.22-4.59-.16-6.72 1.07-2.12 3.09-3.72 5.4-4.22 1.25-.26 2.53-.19 3.77.16v4.11c-.72-.3-1.52-.39-2.28-.27-.79.13-1.53.53-2.06 1.13-.56.62-.87 1.46-.86 2.3.01.81.33 1.57.88 2.15.54.55 1.3.84 2.07.82.78-.02 1.51-.35 2.05-.9.55-.54.87-1.3.88-2.07.02-3.87.01-7.75.01-11.62Z"/>
            </svg>
          </a>
        </div>
      </div>
      <div>
        <div className="text-sm font-semibold mb-3">Produto</div>
        <ul className="space-y-2 text-sm text-slate-300">
          <li><a href="#funcionalidades" className="hover:text-emerald-400">Funcionalidades</a></li>
          <li><a href="#como-funciona" className="hover:text-emerald-400">Como funciona</a></li>
          <li><a href="#planos" className="hover:text-emerald-400">Planos</a></li>
        </ul>
      </div>
      <div>
        <div className="text-sm font-semibold mb-3">Suporte</div>
        <ul className="space-y-2 text-sm text-slate-300">
          <li><a href="#faq" className="hover:text-emerald-400">FAQ</a></li>
          <li><a href="#" className="hover:text-emerald-400">Contato</a></li>
          <li><a href="#" className="hover:text-emerald-400">Reembolso</a></li>
          <li><a href="mailto:suporte@monyxx.com.br" className="hover:text-emerald-400">suporte@monyxx.com.br</a></li>
        </ul>
      </div>
      <div>
        <div className="text-sm font-semibold mb-3">Legal</div>
        <ul className="space-y-2 text-sm text-slate-300">
          <li><button onClick={() => setTermosOpen(true)} className="hover:text-emerald-400 text-left">Termos de uso</button></li>
          <li><button onClick={() => setPrivacidadeOpen(true)} className="hover:text-emerald-400 text-left">Política de privacidade</button></li>
        </ul>
      </div>
    </div>
    <div className="border-t border-slate-800 py-5 text-center text-xs text-slate-400">
      © {new Date().getFullYear()} Monyxx. Todos os direitos reservados.
    </div>
  </footer>
  {privacidadeOpen && <PrivacidadeModal onClose={() => setPrivacidadeOpen(false)} />}
  {termosOpen && <TermosModal onClose={() => setTermosOpen(false)} />}
  </>
  );
};

/* ---------------- LEAD MODAL ---------------- */
const LeadModal = ({ open, onOpenChange, plan }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Preencha nome e e-mail");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, plan, source: "landing" });
      toast.success("Recebemos seu interesse! Em instantes entraremos em contato.");
      setForm({ name: "", email: "", phone: "" });
      onOpenChange(false);
    } catch (err) {
      toast.error("Não foi possível enviar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Garanta seu acesso ao Monyxx</DialogTitle>
          <DialogDescription>
            {plan ? `Plano escolhido: ${plan}. ` : ""}Deixe seus dados que entraremos em contato.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <Input
            placeholder="Seu nome"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="lead-name-input"
          />
          <Input
            type="email"
            placeholder="Seu melhor e-mail"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="lead-email-input"
          />
          <Input
            placeholder="Telefone (opcional)"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            data-testid="lead-phone-input"
          />
          <DialogFooter className="mt-2">
            <Button
              type="submit"
              disabled={loading}
              className="monyx-btn-primary text-white w-full h-11 rounded-full font-semibold"
              data-testid="lead-submit-btn"
            >
              {loading ? "Enviando..." : "Quero meu acesso"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- MAIN ---------------- */
export default function LandingPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const openCta = (plan = null) => {
    // CTAs principais agora levam DIRETO pro sistema — sem pedir email
    window.location.href = "/app";
  };

  return (
    <div className="landing-page min-h-screen bg-[#0A1A2E] text-white overflow-x-hidden" data-testid="landing-page">
      <Navbar onCta={() => openCta()} />
      <MonyxxStats />
      <HowItWorks />
      <PainelSection />
      <Features />
      <TesteHeroRotativo />
      <RemindersSection />
      <TickerInvestimentos />
      <Testimonials />
      <MobileShowcase />
      <Pricing onChoose={(p) => openCta(p)} />
      <FAQ />
      <FinalCta onCta={() => openCta()} />
      <Footer />
      <LeadModal open={modalOpen} onOpenChange={setModalOpen} plan={selectedPlan} />
      <ExitIntentPopup />
      
    </div>
  );
}
